// DateTimeCard.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns'; 
import styled from 'styled-components';
import { purpleColors, violetColors, slateColors, fontFamily } from '../ContentPages/AppLandingPage/styles';
import { format, set } from 'date-fns';
import { CalendarProps } from 'react-calendar';
import StyledCalendar from '../Calendar';
import * as Buttons from '../Controls/Buttons';
import * as Dropdowns from '../Controls/Dropdowns';
import { ReactComponent as LeftArrow } from '../../icons/chevron-left.svg';
import { ReactComponent as RightArrow } from '../../icons/chevron-right.svg';

// Styled components
const FrameContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 95%;
  max-width: 600px;
  max-height: 90%;
  overflow-y: auto;
  position: relative;
  box-sizing: border-box;
  border: 1px solid ${slateColors[100]};
  
  @media (min-width: 640px) {
    padding: 2.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 3rem;
    border-radius: 2rem;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ControlSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1.75rem;
  align-self: stretch;
`;

const TimeSection = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 0.5rem;
  align-self: stretch;
  flex-wrap: wrap;
`;

const DropdownContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AtText = styled.span`
  font-family: ${fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${slateColors[700]};
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

// Get today's date
const today = new Date();
today.setHours(0, 0, 0, 0);

// Define types for react-calendar
interface DateTimeCardProps {
  onClose: () => void;
  onApply: (dateTime: string, type: 'departure' | 'arrival') => void;
  initialDate: string;
  initialTime: string;
  initialType: 'departure' | 'arrival';
}

const DateTimeCard = ({ onClose, onApply, initialDate, initialTime, initialType }: DateTimeCardProps) => {
  const { t, i18n } = useTranslation();
  
  // Map i18n language codes to calendar locale codes
  const getCalendarLocale = (language: string): string => {
    const localeMap: Record<string, string> = {
      'en': 'en-GB',
      'de': 'de-DE',
      'fr': 'fr-FR',
      'nl': 'nl-NL',
    };
    return localeMap[language] || 'en-GB';
  };
  
  // Initialize selectedDate using initialDate if it exists, otherwise use the default
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // Check if initialDate is provided and parse it
    if (initialDate) {
      return parseISO(initialDate);
    }
    return new Date(); // Default to the current date
  }); 

  const getClosestTimeSlot = (date: Date) => {
    const minutes = date.getMinutes();
    const closestHalfHour = minutes < 30 ? 0 : 30;
    return format(set(date, { minutes: closestHalfHour, seconds: 0, milliseconds: 0 }), 'HH:mm');
  };

  // Default to the current time if no initialTime is provided
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    // Default to closest half-hour if no initial time is provided
    return initialTime || getClosestTimeSlot(new Date());
  });

  // Initialize departOrArrive using initialType
  const [departOrArrive, setDepartOrArrive] = useState<'departure' | 'arrival'>(initialType);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const generateTimeOptions = () => {
    const times = [];
    for (let i = 0; i < 24; i++) {
      times.push(`${i.toString().padStart(2, '0')}:00`);
      times.push(`${i.toString().padStart(2, '0')}:30`);
    }
    return times;
  };

  const handleNowClick = () => {
    const now = new Date();
    setSelectedDate(now);
    setSelectedTime(getClosestTimeSlot(now));
    setHasChanges(true);
  };

  // Updated handleDateChange to handle single Date values
  const handleDateChange: CalendarProps['onChange'] = (value, event) => {
    if (value instanceof Date) {
      setSelectedDate(value);
      setHasChanges(true);
    }
  };

  const handleApply = () => {
    const date = format(selectedDate, 'yyyy-MM-dd');
    const dateTime = `${date}T${selectedTime}`;
    onApply(dateTime, departOrArrive);
    setHasChanges(false);
  };

  return (
    <FrameContainer>
      <HeaderSection>
        <Buttons.CloseButton onClick={onClose} disabled={false} />
        <Buttons.TextButtonM onClick={handleNowClick}>{t('dateTime.now')}</Buttons.TextButtonM>
      </HeaderSection>

      <ControlSection>
        <StyledCalendar
          onChange={handleDateChange}
          value={selectedDate}
          locale={getCalendarLocale(i18n.language)}
          selectRange={false}
          minDate={today}
          minDetail={"month"}
          tileClassName={({ date, view }: any) => {
            if (view === 'month') {
              const currentDate = new Date(date);
              currentDate.setHours(0, 0, 0, 0); // Reset time for comparison
              return date < today ? 'react-calendar__tile--disabled' : '';
            }
            return '';
          }}
          prevLabel={<LeftArrow />} // Use custom icon for previous button
          nextLabel={<RightArrow />} // Use custom icon for next button
        />

        <TimeSection>
          <Buttons.SegmentedControlS
            activeValue={departOrArrive}
            options={[
              { label: t('dateTime.departure'), value: 'departure' },
              { label: t('dateTime.arrival'), value: 'arrival' },
            ]}
            onChange={(value) => {
              setDepartOrArrive(value as 'departure' | 'arrival');
              setHasChanges(true);
            }}
          />

          <DropdownContainer>
            <AtText>{t('common.at')}</AtText>

            <Dropdowns.DropdownS
              value={selectedTime}
              onChange={(e) => {
                setSelectedTime(e.target.value);
                setHasChanges(true);
              }}
            >
              {generateTimeOptions().map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Dropdowns.DropdownS>
          </DropdownContainer>
        </TimeSection>
      </ControlSection>

      <Buttons.CTAButtonFilled onClick={handleApply} disabled={!hasChanges}>
        {t('dateTime.apply')}
      </Buttons.CTAButtonFilled>
    </FrameContainer>
  );
};

export default DateTimeCard;